import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { formatCurrency } from '../../utils/formatters';

// Reusable components
const SectionHeader = ({ title }) => (
  <Box display="flex" alignItems="center" mb={2}>
    <AccountBalanceWalletIcon color="primary" sx={{ mr: 1 }} />
    <Typography variant="h6">{title}</Typography>
  </Box>
);

const FinancialRow = ({ label, valueBRL, valueUSD }) => (
  <TableRow>
    <TableCell component="th" scope="row">
      {label}
    </TableCell>
    <TableCell align="right">{formatCurrency(valueBRL, 'BRL')}</TableCell>
    <TableCell align="right">{formatCurrency(valueUSD, 'USD')}</TableCell>
  </TableRow>
);

/**
 * FinancialSummarySection - Displays financial summary information for advisory orders
 * Specific to Advisory order type
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data
 */
const FinancialSummarySection = ({ order }) => {
  // Get tax calculation data from order
  const taxCalculation = order?.latest_tax_calculation || {};

  // Debug output - moved to top level
  useEffect(() => {
    if (!order) return;

    console.log("FinancialSummarySection - Tax Calculation:", taxCalculation);
    console.log("Logistics expense:", {
      frete_rod_interno: taxCalculation.frete_rod_interno,
      logistica_interna: taxCalculation.logistica_interna,
      total_destination_fee_usd: order.total_destination_fee_usd,
      additional_costs: order.additional_costs
    });
    console.log("Advisory fee components:", {
      margem_comercial_brl: taxCalculation.margem_comercial_brl,
      margem_comercial_usd: taxCalculation.margem_comercial_usd,
      acessoria: taxCalculation.acessoria
    });
  }, [taxCalculation, order]);

  if (!order) return null;

  // Calculate exchange rate for USD conversion when only BRL values are available
  const exchangeRate = taxCalculation.exchange_rate_dollar_to_real || 1;

  // Get logistics expenses with fallback to 0
  const logisticsExpensesBRL = taxCalculation.frete_rod_interno || taxCalculation.logistica_interna || 0;
  const logisticsExpensesUSD = logisticsExpensesBRL ? (logisticsExpensesBRL / exchangeRate) : 0;

  // Calculate the combined advisory fee
  const margemComercialBRL = parseFloat(taxCalculation.margem_comercial_brl) || 0;
  const margemComercialUSD = parseFloat(taxCalculation.margem_comercial_usd) || 0;
  const acessoria = parseFloat(taxCalculation.acessoria) || 0;

  const advisoryFeeBRL = margemComercialBRL + acessoria;
  const advisoryFeeUSD = margemComercialUSD + (acessoria / exchangeRate);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <SectionHeader title="FINANCIAL SUMMARY" />
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">BRL</TableCell>
                <TableCell align="right">USD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <FinancialRow
                label="ICMS Guarantee - TTD409"
                valueBRL={taxCalculation.icms_garantia_brl}
                valueUSD={taxCalculation.icms_garantia_usd}
              />
              <FinancialRow
                label="Customs Advisory - AVX"
                valueBRL={advisoryFeeBRL}
                valueUSD={advisoryFeeUSD}
              />
              <FinancialRow
                label="Logistics Expenses"
                valueBRL={logisticsExpensesBRL}
                valueUSD={logisticsExpensesUSD}
              />
              <FinancialRow
                label="Total Disbursement"
                valueBRL={taxCalculation.desembolso_total_brl}
                valueUSD={taxCalculation.desembolso_total_usd}
              />
              <FinancialRow
                label="Tax Credits - Presumed/Real Profit"
                valueBRL={taxCalculation.total_tax_credits_brl}
                valueUSD={taxCalculation.total_tax_credits_usd}
              />
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell component="th" scope="row">
                  <Typography fontWeight="bold">Total Import Cost</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatCurrency(taxCalculation.custo_total_brl)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatCurrency(taxCalculation.custo_total_usd, 'USD')}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default FinancialSummarySection;